import SectionHeader from "@/components/SectionHeader";
import Link from "next/link";
import { euro } from "@/utils/preisAnalyse";

/**
 * "Was kostet eine Fotobox?" — die meistgestellte Frage im Themenfeld.
 *
 * In der Search Console ist "Preis / Kosten" mit rund 30.000 Impressionen in
 * 90 Tagen das größte Cluster der Domain; allein "was kostet eine fotobox"
 * bringt 1.319 Impressionen. Beantwortet hat die Seite das bisher mit einem
 * Satz ohne eine einzige Zahl.
 *
 * Jede Zahl in diesem Abschnitt kommt aus preisAnalyse() und damit aus den
 * Angeboten, die zwei Bildschirmhöhen weiter oben stehen. Nichts davon ist
 * Text, der irgendwann nicht mehr stimmt.
 */

function Tabelle({ titel, spalte, gruppen }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] border-collapse text-left text-sm">
        <caption className="sr-only">{titel}</caption>
        <thead>
          <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
            <th scope="col" className="py-2 pr-4 font-semibold">
              {spalte}
            </th>
            <th scope="col" className="py-2 pr-4 text-right font-semibold">
              Pakete
            </th>
            <th scope="col" className="py-2 pr-4 text-right font-semibold">
              ab
            </th>
            <th scope="col" className="py-2 text-right font-semibold">
              Median
            </th>
          </tr>
        </thead>
        <tbody>
          {gruppen.map((g) => (
            <tr key={g.label} className="border-b border-slate-100 last:border-0">
              <th scope="row" className="py-2.5 pr-4 font-medium text-slate-900">
                {g.label}
              </th>
              <td className="py-2.5 pr-4 text-right tabular-nums text-slate-600">
                {g.anzahl}
              </td>
              <td className="py-2.5 pr-4 text-right tabular-nums text-slate-600">
                {euro(g.min)} €
              </td>
              <td className="py-2.5 text-right font-semibold tabular-nums text-slate-900">
                {euro(g.median)} €
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PreisSektion({ analyse, stand }) {
  if (!analyse) return null;

  const { gesamt, nachDruck, nachLieferung, nachKapazitaet } = analyse;
  const digital = nachDruck.find((g) => g.label.includes("digital"));
  const kontingent = nachDruck.find((g) => g.label.includes("Kontingent"));
  const flatrate = nachDruck.find((g) => g.label.includes("Flatrate"));
  const versand = nachLieferung.find((g) => g.label.includes("Versand"));
  const vorOrt = nachLieferung.find((g) => g.label.includes("vor Ort"));

  const H3 = ({ children, id }) => (
    <h3 id={id} className="mt-10 text-xl font-semibold text-slate-900">
      {children}
    </h3>
  );

  return (
    <SectionHeader
      id="preise"
      title="Was kostet eine Fotobox?"
      subtitle={`${analyse.anzahlPakete} Pakete von ${analyse.anzahlBoxen} Fotoboxen — ausgewertet, nicht geschätzt.`}
    >
      <div className="mx-auto mt-8 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        <p>
          Das günstigste Paket im Vergleich kostet{" "}
          <strong className="font-semibold text-slate-900">
            {euro(gesamt.min)} €
          </strong>
          , das teuerste{" "}
          <strong className="font-semibold text-slate-900">
            {euro(gesamt.max)} €
          </strong>
          . Die Hälfte aller Pakete liegt unter{" "}
          <strong className="font-semibold text-slate-900">
            {euro(gesamt.median)} €
          </strong>{" "}
          — das ist der Wert, an dem du dich orientieren kannst, solange du
          noch keine genaue Vorstellung hast. Warum die Spanne so groß ist,
          entscheidet sich an drei Punkten.
        </p>
        {gesamt.min < 100 && digital && (
          <p className="mt-3">
            <Link
              href="/fotobox-mieten-unter-100-eur"
              className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
            >
              Fotoboxen unter 100 €
            </Link>{" "}
            gibt es tatsächlich — es sind durchweg digitale Pakete ohne
            Drucker. Wer Ausdrucke möchte, sollte mit mindestens{" "}
            {euro(
              (nachDruck.find((g) => g.label.includes("Kontingent")) || {}).min ||
                0
            )}{" "}
            € rechnen.
          </p>
        )}

        <H3 id="preis-drucker">1. Drucker oder nur digital</H3>
        <p className="mb-4">
          Der Drucker ist der größte einzelne Posten. Zwischen einem rein
          digitalen Paket und einem mit Druckkontingent liegen im Median{" "}
          <strong className="font-semibold text-slate-900">
            {euro(kontingent && digital ? kontingent.median - digital.median : 0)} €
          </strong>
          .
        </p>
        <Tabelle
          titel="Preise nach Druckoption"
          spalte="Ausstattung"
          gruppen={nachDruck}
        />
        <p className="mt-4">
          <Link
            href="/fotobox-mieten-mit-sofortdruck"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Alle Fotoboxen mit Sofortdruck ansehen
          </Link>
        </p>
        {flatrate && kontingent && flatrate.median < kontingent.median && (
          <p className="mt-4">
            Bemerkenswert: Die Druck-Flatrate liegt mit {euro(flatrate.median)} € im
            Median <em>unter</em> dem festen Kontingent ({euro(kontingent.median)} €).
            Der Grund ist nicht der Drucker, sondern die Herkunft — Flatrates
            kommen häufiger von Versandanbietern, die insgesamt günstiger
            kalkulieren. Wer viel drucken will, zahlt dafür also nicht
            automatisch drauf.
          </p>
        )}

        <H3 id="preis-lieferung">2. Versand oder Anbieter vor Ort</H3>
        {versand && vorOrt && (
          <>
            <p className="mb-4">
              Zwischen beiden Wegen liegen im Median{" "}
              <strong className="font-semibold text-slate-900">
                {euro(vorOrt.median - versand.median)} €
              </strong>
              .
            </p>
            <Tabelle
              titel="Preise nach Lieferweg"
              spalte="Lieferweg"
              gruppen={nachLieferung}
            />
            <p className="mt-4">
              Dafür bringt der Anbieter vor Ort die Box, baut sie auf und holt
              sie wieder ab. Bei der Versandbox übernimmst du das selbst — dafür
              ist der Versand bei allen {analyse.versand.gesamt} Versandboxen im
              Vergleich kostenlos. Bei regionalen Anbietern kommt je nach
              Entfernung eine Anfahrtspauschale dazu; die rechnen wir im
              Vergleich mit deiner Postleitzahl aus, bevor du anfragst.
            </p>
          </>
        )}

        {nachKapazitaet.length > 1 && (
          <>
            <H3 id="preis-kapazitaet">3. Wie viele Bilder gedruckt werden</H3>
            <p className="mb-4">
              Bei Druckpaketen bestimmt die Papierkapazität den Preis mit. Als
              Faustregel gilt: Rechne mit zwei bis drei Ausdrucken pro Gast —
              bei 80 Gästen also mit rund 200 Bildern.
            </p>
            <Tabelle
              titel="Preise nach Druckkapazität"
              spalte="Kapazität"
              gruppen={nachKapazitaet}
            />
          </>
        )}

        <H3 id="preis-zusatz">Womit du zusätzlich rechnen solltest</H3>
        <ul className="mt-3 list-disc space-y-2 pl-5 marker:text-slate-400">
          <li>
            <strong className="font-semibold text-slate-900">Anfahrt</strong> bei
            regionalen Anbietern — abhängig von der Entfernung, im Vergleich für
            deine PLZ ausgewiesen.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">Umbuchung</strong>{" "}
            ist bei {analyse.umbuchung.kostenlos} von {analyse.umbuchung.gesamt}{" "}
            Fotoboxen kostenlos. Bei den übrigen kann eine Gebühr anfallen, wenn
            sich euer Termin verschiebt.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">
              Nachdrucken
            </strong>{" "}
            über das gebuchte Kontingent hinaus — bei Flatrate-Paketen entfällt
            das.
          </li>
        </ul>

        <p className="mt-6 text-xs text-slate-500">
          Alle Zahlen aus den {analyse.anzahlPakete} Paketen im Vergleich,
          Stand {stand}. Median statt Durchschnitt, damit einzelne
          Sonderangebote das Bild nicht verzerren.
        </p>
      </div>
    </SectionHeader>
  );
}
